import React, { createContext, useState, useEffect, useContext } from 'react';

// Create theme context
const ThemeContext = createContext();

// Define theme color schemes
const themes = {
  light: {
    name: 'light',
    background: '#f6f1ea',
    surface: '#fffaf4',
    primary: '#2f4c4c',
    secondary: '#355f7a',
    text: {
      primary: '#1c1a18',
      secondary: '#4d4a46',
      disabled: '#7a746d',
      inverse: '#fffaf4'
    },
    border: '#d7cec4',
    elevation: {
      0: 'none',
      1: '0 1px 2px rgba(31,23,16,0.06)',
      2: '0 2px 6px rgba(31,23,16,0.10)',
      3: '0 8px 20px rgba(31,23,16,0.12)'
    }
  },
  dark: {
    name: 'dark',
    background: '#121212', // Slightly off-black for better contrast
    surface: '#1e1e1e',
    primary: '#81a4a4', // Lighter version of your brand color
    secondary: '#61a5d4',
    text: {
      primary: '#e0e0e0',
      secondary: '#b0b0b0',
      disabled: '#6c6c6c',
      inverse: '#121212'
    },
    border: '#333333',
    elevation: {
      0: 'none',
      1: '0 1px 2px rgba(0,0,0,0.15)',
      2: '0 2px 4px rgba(0,0,0,0.2)',
      3: '0 4px 8px rgba(0,0,0,0.25)'
    }
  }
};

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme;
    return 'dark';
  });

  const theme = themes[themeMode];
  
  const toggleTheme = () => {
    setThemeMode(prev => {
      const newTheme = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', newTheme);
      return newTheme;
    });
  };
  
  useEffect(() => {
    document.body.setAttribute('data-theme', themeMode);
    document.documentElement.style.setProperty('--background-color', theme.background);
    document.documentElement.style.setProperty('--surface-color', theme.surface);
    document.documentElement.style.setProperty('--primary-color', theme.primary);
    document.documentElement.style.setProperty('--text-color', theme.text.primary);
    document.documentElement.style.setProperty('--secondary-text-color', theme.text.secondary);
    document.documentElement.style.setProperty('--border-color', theme.border);
  }, [theme, themeMode]);

  return (
    <ThemeContext.Provider value={{ theme, themeMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook for using the theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};